// EntityFetcher.js
// Singleton — raw I/O + DTO mapping only, no caching/notify here (Engine's job).
// BE (EcoFlowStreamService) field names differ from ours: bName is the entity identity
// (our rk), totalEnergy->totalKwh, totalCost->totalTibberCost. The row's own BE `rk`
// is NOT the entity identity — it's a per-day id, kept as costRk for the later
// getPowerQuarters('owner'=costRk) graph call, see EcoFlowStreamService JSDoc.
import CoreBase from '../../../services/engines/CoreBase';
import ecoFlowStreamService from '../../../services/EcoFlowStreamService';
import entityService from '../../../services/EntityService';

const _name = 'EntityFetcher';

class EntityFetcher extends CoreBase {
    _name = _name;

    /**
     * Fetches daily cost/energy rows.
     * @param {string} [name] - entity name (bName), default 'all' entities
     * @param {number} [start] - timeframe start ems, 0 = today
     * @param {number} [end] - timeframe end ems, 0 = start + 1 day
     * @returns {Promise<object[]>} mapped rows, 1 per entity per day
     */
    fetch = async (name = 'all', start = 0, end = 0) => {
        try {
            const response = await ecoFlowStreamService.getPowerCosts(name, start, end);
            const items = response?.data?.items ?? [];
            return items.map(this._map);
        } catch (error) {
            this.logError('fetch', error.message);
            return [];
        }
    };

    /**
     * Fetches the full current state of 1 entity (cache-miss seed / refresh-button).
     * BE returns `changes: [{name, value}]`, same shape as the SignalR push envelope —
     * mapped here to a flat row so EntityEngine can write it with 1 setRow() call.
     * @param {string} rk - entity identity, e.g. 'tapo-01'
     * @returns {Promise<object|null>} flat row {rk, ems, ...fields}, or null if unknown
     */
    fetchCardState = async (rk) => {
        try {
            const response = await entityService.getEntityCard(rk);
            const item = response?.data?.items?.[0];
            if (!item) return null;

            const fields = Object.fromEntries(item.changes.map(({ name, value }) => [name, value]));
            return { rk: item.rk, ems: item.ems, ...fields };
        } catch (error) {
            this.logError('fetchCardState', error.message);
            return null;
        }
    };

    // Fields BE doesn't provide yet (peak/online/source/sourceLabel/parentRk/power/
    // totalBatCost/diffCost) are left out on purpose — EntityTypeRegistry renders "-"
    // for those. type is hardcoded here: every row from this service is a SmartPlug.
    // NOTE: geen dayEms/isToday hier — welke dag getoond wordt bepaalt EntityCard zelf
    // via zijn eigen dayOffset-state, dat hoefde nooit ook nog eens op de row te staan.
    _map = (item) => ({
        rk: item.bName,
        type: 'SmartPlug',
        costRk: item.rk,
        totalKwh: item.totalEnergy,
        totalTibberCost: item.totalCost,
        avgPrice: item.avgPrice,
        ems: Date.now(),
    });
}

const entityFetcher = new EntityFetcher();
export default entityFetcher;
